import { Controller } from "@hotwired/stimulus"
import { GmpMap } from "@/utils/map_interfaces"
import { waitForMap } from "@/utils/map_utils"

// The pin a visit is recorded at, shown on the sheet rather than described in
// text: a worker can see whether the fix landed on the right side of the block,
// and drag it if it did not. The coordinates arrive from the outreach map as
// query params and are posted from the hidden fields this controller rewrites.
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "status"]
  // Set when the sheet opened without a pin (from a neighbor profile): once
  // the phone answers, the sheet reloads at this URL with the fix appended so
  // the map, the pin and the nearby camps render server-side.
  static values = { reloadUrl: String }

  declare readonly mapTarget: GmpMap
  declare readonly hasMapTarget: boolean
  declare readonly latitudeTarget: HTMLInputElement
  declare readonly longitudeTarget: HTMLInputElement
  declare readonly statusTarget: HTMLElement
  declare readonly hasStatusTarget: boolean
  declare readonly reloadUrlValue: string

  private marker: google.maps.marker.AdvancedMarkerElement | null = null

  async connect() {
    const position = this.position()
    if (!position) {
      if (this.reloadUrlValue) this.locate()
      return
    }
    if (!this.hasMapTarget) return

    await waitForMap(this.mapTarget)

    const map = this.mapTarget.innerMap
    // Same as every other map in the app: the sheet is 200px tall and has no
    // room for Google's default controls.
    map.set("disableDefaultUI", true)

    this.marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      gmpDraggable: true
    })
    this.marker.addListener("dragend", (event: google.maps.MapMouseEvent) => this.sync(event.latLng))
  }

  disconnect() {
    if (this.marker) this.marker.map = null
    this.marker = null
  }

  // The pin arrives as the map's centre, which is where the worker was looking,
  // not necessarily where they are standing. One tap puts it under their feet.
  locate() {
    if (!navigator.geolocation) return this.failed()

    navigator.geolocation.getCurrentPosition((fix) => {
      const position = { lat: fix.coords.latitude, lng: fix.coords.longitude }
      this.latitudeTarget.value = position.lat.toFixed(6)
      this.longitudeTarget.value = position.lng.toFixed(6)
      if (this.reloadUrlValue) return this.reload()
      if (this.marker) this.marker.position = position
      if (this.hasMapTarget) this.mapTarget.innerMap?.panTo(position)
    }, () => this.failed(), { enableHighAccuracy: true, timeout: 10000 })
  }

  private reload() {
    const url = new URL(this.reloadUrlValue, window.location.origin)
    url.searchParams.set("latitude", this.latitudeTarget.value)
    url.searchParams.set("longitude", this.longitudeTarget.value)
    const frame = this.element.closest("turbo-frame") as (HTMLElement & { src: string }) | null
    if (frame) frame.src = url.toString()
    else window.location.assign(url.toString())
  }

  private failed() {
    if (this.hasStatusTarget) this.statusTarget.textContent = this.statusTarget.dataset.failedText || ""
  }

  private position(): google.maps.LatLngLiteral | null {
    const lat = parseFloat(this.latitudeTarget.value)
    const lng = parseFloat(this.longitudeTarget.value)

    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
  }

  // Six decimals is ~10cm — finer than any phone fix, and short enough that the
  // posted params stay readable in a log.
  private sync(latLng: google.maps.LatLng | null) {
    if (!latLng) return

    this.latitudeTarget.value = latLng.lat().toFixed(6)
    this.longitudeTarget.value = latLng.lng().toFixed(6)
  }
}
